import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import {
  useGetBobcatTagsList,
  useListBobcatOfferGroups,
} from '@/api/trafikportalen';
import { CollectionTicketClaimRevokeStrategyDTO } from '@/api/trafikportalen/models/collectionTicketClaimRevokeStrategyDTO';
import Button from '../core/Button';
import Input from '../core/Input';
import CheckBox from '../core/Checkbox';
import ToggleButtonGroup from '../core/ToggleButtonGroup';
import { DialogWindow } from '../Dialog';
import { MultiSelectableList, SelectableList } from '../SelectableList';
import { ErrorBanner } from '../core/ErrorBanner';
import { WarningBanner } from '../core/WarningBanner';
import { collator } from '@/utils/collator';
import {
  hasPeriodActiveThisOrPreviousMonth,
  toPeriodEndTimestamp,
  toPeriodStartTimestamp,
} from '@/helpers/periodEndDate';
import { organizationIdsWithSamePeriod } from '@/helpers/periodConfiguration';
import { findOverlappingClaims } from '@/helpers/periodOverlap';
import { COLLECTION_TYPE_LABELS } from '@/helpers/collectionTypes';
import {
  NO_PERIOD_TAG_CHANGES,
  isTagRemovalBlocked,
  relationalTagChoices,
} from '@/helpers/periodProducts';
import { useOffersSharedByOrganizations } from '@/hooks/useOffersSharedByOrganizations';
import { useOrganizationsAllowedTags } from '@/hooks/useOrganizationsAllowedTags';
import { IconCheck, IconWarningTriangle } from '@/icons';
import type {
  CollectionTicketClaimDTO,
  OrganizationDTO,
} from '@/api/trafikportalen';
import type { CollectionTicketClaimTypeDTO } from '@/api/trafikportalen/models/collectionTicketClaimTypeDTO';
import type { ToggleButtonOption } from '../core/ToggleButtonGroup';
import type { StudentCollectionType } from '@/helpers/collectionTypes';
import type { PeriodTagChanges } from '@/helpers/periodProducts';
import type { CreatePeriodFormData } from './CreatePeriodDialog';

interface TenantCreatePeriodDialogProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  onSubmit: (
    data: CreatePeriodFormData,
    organizationIds: string[],
    nonInvoiced: boolean,
    tags: PeriodTagChanges,
  ) => Promise<void>;
  isSubmitting: boolean;
  claimType: CollectionTicketClaimTypeDTO;
  collectionType: StudentCollectionType;
  organizationIds: string[];
  selectableOrganizations?: OrganizationDTO[];
  organizationsById?: Map<string, OrganizationDTO>;
  existingClaims?: CollectionTicketClaimDTO[];
}

const MAX_LISTED_SELF_MANAGED_ORGANIZATIONS = 3;

type OfferSelectionUiMode = 'loading' | 'empty' | 'single' | 'multiple';

const parseStinaProductId = (groupKey: string | null): string | null => {
  if (!groupKey) return null;
  const underscoreIndex = groupKey.indexOf('_');
  if (underscoreIndex === -1) return null;
  const productId = groupKey.slice(underscoreIndex + 1);
  return productId.length > 0 ? productId : null;
};

export const TenantCreatePeriodDialog = ({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  claimType,
  collectionType,
  organizationIds,
  selectableOrganizations,
  organizationsById,
  existingClaims,
}: TenantCreatePeriodDialogProps) => {
  const { formatMessage, formatList } = useIntl();
  const isChildAndYouth = collectionType === 'school-child-and-youth';
  const today = DateTime.now().toFormat('yyyy-MM-dd');

  const [formFrom, setFormFrom] = useState(today);
  const [formTo, setFormTo] = useState('');
  const [formRevokeStrategy, setFormRevokeStrategy] =
    useState<CollectionTicketClaimRevokeStrategyDTO>('revoke');
  const [formRelational, setFormRelational] = useState(!isChildAndYouth);
  const [formNonInvoiced, setFormNonInvoiced] = useState(false);
  const [formOfferStinaProductId, setFormOfferStinaProductId] = useState<
    string | null
  >(null);
  const [addedTagIds, setAddedTagIds] = useState<string[]>([]);
  const [removedTagIds, setRemovedTagIds] = useState<string[]>([]);
  const [pickedOrgIds, setPickedOrgIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [orgQuery, setOrgQuery] = useState('');

  const targetOrganizationIds = useMemo(
    () => (selectableOrganizations ? [...pickedOrgIds] : organizationIds),
    [selectableOrganizations, pickedOrgIds, organizationIds],
  );

  const selfManagedOrganizations = useMemo(() => {
    const lookup =
      organizationsById ??
      new Map(
        (selectableOrganizations ?? []).map((organization) => [
          organization.id,
          organization,
        ]),
      );
    return targetOrganizationIds
      .map((id) => lookup.get(id))
      .filter(
        (organization): organization is OrganizationDTO =>
          organization?.selfmanaged_school_claims === true,
      )
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [organizationsById, selectableOrganizations, targetOrganizationIds]);

  const selfManagedOrganizationIds = useMemo(
    () => selfManagedOrganizations.map((organization) => organization.id),
    [selfManagedOrganizations],
  );
  const allProductsAvailable =
    targetOrganizationIds.length > 0 && selfManagedOrganizationIds.length === 0;

  const relational = formRelational && !isChildAndYouth;
  const {
    allowedTags,
    isLoading: isLoadingAllowedTags,
    isError: allowedTagsAccessFailed,
  } = useOrganizationsAllowedTags(targetOrganizationIds, open && relational);
  const {
    data: allTags,
    isLoading: isLoadingAllTags,
    isError: allTagsAccessFailed,
  } = useGetBobcatTagsList({ query: { enabled: open && relational } });
  const isLoadingTags = isLoadingAllowedTags || isLoadingAllTags;
  const relationalProductsAccessFailed =
    allowedTagsAccessFailed || allTagsAccessFailed;

  const tagOrganizations = useMemo(() => {
    if (!allowedTags) return null;
    const selfManagedIds = new Set(selfManagedOrganizationIds);
    return targetOrganizationIds.map((id, index) => ({
      id,
      selfManaged: selfManagedIds.has(id),
      recentPeriod:
        !existingClaims ||
        hasPeriodActiveThisOrPreviousMonth(
          existingClaims.filter((claim) => claim.organization_id === id),
        ),
      allowed: allowedTags[index] ?? [],
    }));
  }, [
    allowedTags,
    existingClaims,
    selfManagedOrganizationIds,
    targetOrganizationIds,
  ]);

  const tagChoices = useMemo(
    () =>
      tagOrganizations && allTags
        ? relationalTagChoices(allTags, tagOrganizations, collectionType)
        : null,
    [allTags, collectionType, tagOrganizations],
  );

  const periodTagIds = useMemo(
    () =>
      (tagChoices?.available ?? [])
        .map((tag) => tag.id)
        .filter((id) =>
          tagChoices?.activeIds.includes(id)
            ? !removedTagIds.includes(id)
            : addedTagIds.includes(id),
        ),
    [addedTagIds, removedTagIds, tagChoices],
  );
  const deselectedTagIds = useMemo(
    () =>
      (tagChoices?.activeIds ?? []).filter((id) => removedTagIds.includes(id)),
    [removedTagIds, tagChoices],
  );
  const tagRemovalBlocked =
    !!tagOrganizations &&
    isTagRemovalBlocked(tagOrganizations, collectionType, deselectedTagIds);
  const periodTagChanges = useMemo(
    (): PeriodTagChanges => ({
      tagIds: periodTagIds,
      removedTagIds: deselectedTagIds,
      removableOrganizationIds: (tagOrganizations ?? [])
        .filter((organization) => !organization.selfManaged)
        .filter((organization) => !organization.recentPeriod)
        .map((organization) => organization.id),
    }),
    [deselectedTagIds, periodTagIds, tagOrganizations],
  );
  const tagOptions = useMemo(
    () =>
      (tagChoices?.available ?? [])
        .map((tag) => ({ id: tag.id, label: tag.title }))
        .sort((a, b) => collator.compare(a.label, b.label)),
    [tagChoices],
  );
  const hasRelationalProducts = tagChoices
    ? tagChoices.everyOrganizationHasTags || tagChoices.available.length > 0
    : undefined;
  const handleTagSelectionChange = useCallback(
    (ids: string[]) => {
      const activeIds = tagChoices?.activeIds ?? [];
      setAddedTagIds(ids.filter((id) => !activeIds.includes(id)));
      setRemovedTagIds(activeIds.filter((id) => !ids.includes(id)));
    },
    [tagChoices],
  );

  const {
    offers: sharedOffers,
    isLoading: isLoadingSharedOffers,
    isError: sharedOffersAccessFailed,
  } = useOffersSharedByOrganizations(
    selfManagedOrganizationIds,
    collectionType,
    open && !relational,
  );
  const {
    data: allOffers,
    isLoading: isLoadingAllOffers,
    isError: allOffersAccessFailed,
  } = useListBobcatOfferGroups({
    query: { enabled: open && !relational && allProductsAvailable },
  });
  const isLoadingOffers = allProductsAvailable
    ? isLoadingAllOffers
    : isLoadingSharedOffers;
  const offersAccessFailed = allProductsAvailable
    ? allOffersAccessFailed
    : sharedOffersAccessFailed;

  const offerOptions = useMemo(
    () =>
      (allProductsAvailable
        ? (allOffers ?? []).filter((group) => group.sales_channel === 'school')
        : sharedOffers
      )
        .map((group) => ({
          id: group.group_key,
          label: group.title,
        }))
        .sort((a, b) => collator.compare(a.label, b.label)),
    [allOffers, allProductsAvailable, sharedOffers],
  );

  const offerSelectionUiMode = useMemo((): OfferSelectionUiMode => {
    if (isLoadingOffers) return 'loading';
    if (offerOptions.length === 0) return 'empty';
    if (offerOptions.length === 1) return 'single';
    return 'multiple';
  }, [isLoadingOffers, offerOptions]);

  const parsedStinaProductId = useMemo(
    () => parseStinaProductId(formOfferStinaProductId),
    [formOfferStinaProductId],
  );

  const offerFormatError =
    !relational && formOfferStinaProductId && !parsedStinaProductId
      ? formatMessage({
          defaultMessage:
            'Erbjudandet har ett oväntat format och kan inte användas. Kontakta support.',
        })
      : undefined;

  const resetForm = useCallback(() => {
    setFormFrom(today);
    setFormTo('');
    setFormRevokeStrategy('revoke');
    setFormRelational(!isChildAndYouth);
    setFormNonInvoiced(false);
    setFormOfferStinaProductId(null);
    setAddedTagIds([]);
    setRemovedTagIds([]);
    setPickedOrgIds(new Set());
    setOrgQuery('');
  }, [isChildAndYouth, today]);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      resetForm();
    }
    wasOpenRef.current = open;
  }, [open, resetForm]);

  useEffect(() => {
    if (relational || isLoadingOffers) return;

    const hasValidSelection =
      formOfferStinaProductId !== null &&
      offerOptions.some((option) => option.id === formOfferStinaProductId);

    if (offerOptions.length === 0) {
      if (formOfferStinaProductId !== null) {
        setFormOfferStinaProductId(null);
      }
      return;
    }

    const firstId = offerOptions[0].id;
    if (offerOptions.length === 1 || !hasValidSelection) {
      if (formOfferStinaProductId !== firstId) {
        setFormOfferStinaProductId(firstId);
      }
    }
  }, [relational, isLoadingOffers, offerOptions, formOfferStinaProductId]);

  const revokeStrategyOptions: ToggleButtonOption[] = useMemo(
    () => [
      {
        value: 'revoke',
        label: formatMessage({ defaultMessage: 'Makulera' }),
        tooltip: formatMessage({
          defaultMessage: 'Biljetter makuleras utan återbetalning',
        }),
      },
      {
        value: 'none',
        label: formatMessage({ defaultMessage: 'Inget' }),
        tooltip: formatMessage({
          defaultMessage: 'Biljetter makuleras inte',
        }),
      },
    ],
    [formatMessage],
  );

  const ticketKindOptions: ToggleButtonOption[] = useMemo(
    () => [
      {
        value: 'relational',
        label: formatMessage({ defaultMessage: 'Relationsbunden' }),
        tooltip: formatMessage({
          defaultMessage:
            'Nyttja adressinformationen på varje elev för att automatiskt söka fram rätt biljett.',
        }),
      },
      {
        value: 'static',
        label: formatMessage({ defaultMessage: 'Statisk' }),
        tooltip: formatMessage({
          defaultMessage: 'Alla elever får samma biljett.',
        }),
      },
    ],
    [formatMessage],
  );

  const fromError = useMemo(() => {
    if (!formFrom) return undefined;
    if (formFrom < today) {
      return formatMessage({
        defaultMessage: 'Startdatum måste vara dagens datum eller senare',
      });
    }
    return undefined;
  }, [formFrom, today, formatMessage]);

  const toError = useMemo(() => {
    if (!formTo) return undefined;
    if (formFrom && formTo <= formFrom) {
      return formatMessage({
        defaultMessage: 'Slutdatum måste vara efter startdatum',
      });
    }
    return undefined;
  }, [formFrom, formTo, formatMessage]);

  const filteredOrganizations = useMemo(() => {
    if (!selectableOrganizations) return [];
    const needle = orgQuery.trim().toLocaleLowerCase('sv');
    return [...selectableOrganizations]
      .filter((organization) => {
        if (!needle) return true;
        return (
          organization.name.toLocaleLowerCase('sv').includes(needle) ||
          organization.organization_number
            .toLocaleLowerCase('sv')
            .includes(needle)
        );
      })
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [selectableOrganizations, orgQuery]);

  const allFilteredSelected =
    filteredOrganizations.length > 0 &&
    filteredOrganizations.every((organization) =>
      pickedOrgIds.has(organization.id),
    );
  const someFilteredSelected = filteredOrganizations.some((organization) =>
    pickedOrgIds.has(organization.id),
  );

  const periodDraft = useMemo(() => {
    if (!formFrom || !formTo || toError) return null;
    if (!relational && !parsedStinaProductId) return null;
    return {
      valid_from: toPeriodStartTimestamp(formFrom),
      valid_to: toPeriodEndTimestamp(formTo, false),
      type: claimType,
      revoke_strategy: formRevokeStrategy,
      reissuance_allowed: true,
      relational: relational,
      non_invoiced: formNonInvoiced,
      ...(!relational && parsedStinaProductId
        ? { stina_product_id: parsedStinaProductId }
        : {}),
    };
  }, [
    formFrom,
    formTo,
    toError,
    relational,
    parsedStinaProductId,
    claimType,
    formRevokeStrategy,
    formNonInvoiced,
  ]);

  const duplicateOrganizationIds = useMemo(() => {
    if (!periodDraft) return [];
    return organizationIdsWithSamePeriod(
      existingClaims,
      targetOrganizationIds,
      periodDraft,
    );
  }, [periodDraft, existingClaims, targetOrganizationIds]);

  const overlappingOrgCount = useMemo(() => {
    if (!periodDraft) return 0;
    const targetIds = new Set(targetOrganizationIds);
    const duplicateIds = new Set(duplicateOrganizationIds);
    const overlapping = findOverlappingClaims(
      { from: periodDraft.valid_from, to: periodDraft.valid_to },
      existingClaims?.filter(
        (claim) =>
          targetIds.has(claim.organization_id) &&
          !duplicateIds.has(claim.organization_id),
      ),
    );
    return new Set(overlapping.map((claim) => claim.organization_id)).size;
  }, [
    periodDraft,
    existingClaims,
    targetOrganizationIds,
    duplicateOrganizationIds,
  ]);

  const isFormValid = useMemo(() => {
    if (targetOrganizationIds.length < 1) return false;
    if (!formFrom || !formTo) return false;
    if (fromError || toError) return false;
    if (relational && hasRelationalProducts !== true) return false;
    if (
      relational &&
      !!tagChoices?.available.length &&
      periodTagIds.length === 0
    ) {
      return false;
    }
    if (!relational && (isLoadingOffers || offersAccessFailed)) return false;
    if (!relational && !parsedStinaProductId) return false;
    if (
      !relational &&
      !offerOptions.some((option) => option.id === formOfferStinaProductId)
    ) {
      return false;
    }
    if (duplicateOrganizationIds.length > 0) return false;
    return true;
  }, [
    targetOrganizationIds.length,
    formFrom,
    formTo,
    fromError,
    toError,
    relational,
    hasRelationalProducts,
    tagChoices,
    periodTagIds.length,
    isLoadingOffers,
    offersAccessFailed,
    parsedStinaProductId,
    offerOptions,
    formOfferStinaProductId,
    duplicateOrganizationIds.length,
  ]);

  const handleSelectOffer = useCallback((productSetId: string) => {
    setFormOfferStinaProductId(productSetId);
  }, []);

  const handleCreate = useCallback(async () => {
    if (!isFormValid) return;

    await onSubmit(
      {
        valid_from: toPeriodStartTimestamp(formFrom),
        valid_to: toPeriodEndTimestamp(formTo, false),
        type: claimType,
        revoke_strategy: formRevokeStrategy,
        reissuance_allowed: true,
        relational: relational,
        ...(!relational && parsedStinaProductId
          ? { stina_product_id: parsedStinaProductId }
          : {}),
      },
      targetOrganizationIds,
      formNonInvoiced,
      relational ? periodTagChanges : NO_PERIOD_TAG_CHANGES,
    );
  }, [
    isFormValid,
    onSubmit,
    targetOrganizationIds,
    formFrom,
    formTo,
    claimType,
    formRevokeStrategy,
    relational,
    parsedStinaProductId,
    formNonInvoiced,
    periodTagChanges,
  ]);

  const categoryName = formatMessage(COLLECTION_TYPE_LABELS[collectionType]);

  return (
    <DialogWindow
      open={open}
      onOpenChange={onOpenChange}
      title={formatMessage({ defaultMessage: 'Ny period' })}
      content={
        <div className="mx-auto flex w-full flex-col gap-10">
          <p className="text-text-900">
            {selectableOrganizations ? (
              <FormattedMessage
                defaultMessage="Ange perioden och markera vilka organisationer i kategorin {category} som den ska gälla för."
                values={{ category: categoryName }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Skapa samma period för {count, plural, one {# vald organisation} other {# valda organisationer}} i kategorin {category}."
                values={{
                  count: targetOrganizationIds.length,
                  category: categoryName,
                }}
              />
            )}
          </p>

          {selectableOrganizations && (
            <div className="flex flex-col gap-3">
              <p className="text-base font-medium text-text-900">
                <FormattedMessage defaultMessage="Organisationer" />
              </p>
              <Input
                id="tenant-create-org-search"
                type="text"
                label={formatMessage({ defaultMessage: 'Sök organisation' })}
                value={orgQuery}
                onChange={(event) => setOrgQuery(event.target.value)}
              />
              {filteredOrganizations.length > 0 && (
                <CheckBox
                  id="tenant-create-select-all"
                  label={formatMessage({
                    defaultMessage: 'Markera alla i listan',
                  })}
                  checked={
                    allFilteredSelected
                      ? true
                      : someFilteredSelected
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={(checked) => {
                    setPickedOrgIds((prev) => {
                      const next = new Set(prev);
                      for (const organization of filteredOrganizations) {
                        if (checked === true) next.add(organization.id);
                        else next.delete(organization.id);
                      }
                      return next;
                    });
                  }}
                />
              )}
              {filteredOrganizations.length === 0 ? (
                <p className="italic text-text-500">
                  <FormattedMessage defaultMessage="Inga organisationer att visa." />
                </p>
              ) : (
                <ul className="flex max-h-52 flex-col gap-2 overflow-y-auto">
                  {filteredOrganizations.map((organization) => (
                    <li key={organization.id}>
                      <CheckBox
                        id={`tenant-create-org-${organization.id}`}
                        label={organization.name}
                        description={organization.organization_number}
                        checked={pickedOrgIds.has(organization.id)}
                        onCheckedChange={(checked) => {
                          setPickedOrgIds((prev) => {
                            const next = new Set(prev);
                            if (checked === true) next.add(organization.id);
                            else next.delete(organization.id);
                            return next;
                          });
                        }}
                      />
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-text-700">
                <FormattedMessage
                  defaultMessage="{count, plural, one {# organisation markerad} other {# organisationer markerade}}"
                  values={{ count: pickedOrgIds.size }}
                />
              </p>
            </div>
          )}

          {selfManagedOrganizations.length > 0 && (
            <WarningBanner>
              {selfManagedOrganizations.length <=
              MAX_LISTED_SELF_MANAGED_ORGANIZATIONS ? (
                <FormattedMessage
                  defaultMessage="{names} hanterar sina egna perioder. Kontrollera med {count, plural, one {organisationen} other {organisationerna}} innan du skapar en period åt {count, plural, one {den} other {dem}}."
                  values={{
                    names: formatList(
                      selfManagedOrganizations.map(
                        (organization) => organization.name,
                      ),
                      { type: 'conjunction' },
                    ),
                    count: selfManagedOrganizations.length,
                  }}
                />
              ) : (
                <FormattedMessage
                  defaultMessage="{count} av de valda organisationerna hanterar sina egna perioder. Kontrollera med organisationerna innan du skapar en period åt dem."
                  values={{ count: selfManagedOrganizations.length }}
                />
              )}
            </WarningBanner>
          )}

          <div className="flex flex-wrap gap-8">
            <Input
              id="tenant-period-from"
              label={formatMessage({ defaultMessage: 'Startdatum' })}
              type="date"
              value={formFrom}
              min={today}
              error={fromError}
              helperText={formatMessage({
                defaultMessage: 'Gäller från dagens början',
              })}
              onChange={(e) => setFormFrom(e.target.value)}
            />
            <Input
              id="tenant-period-to"
              label={formatMessage({ defaultMessage: 'Slutdatum' })}
              type="date"
              value={formTo}
              min={formFrom || today}
              error={toError}
              helperText={formatMessage({
                defaultMessage: 'Gäller till dagens slut',
              })}
              onChange={(e) => setFormTo(e.target.value)}
            />
          </div>

          {duplicateOrganizationIds.length > 0 && (
            <ErrorBanner id="tenant-duplicate-period">
              <FormattedMessage
                defaultMessage="Den här perioden finns redan hos {count, plural, one {# organisation} other {# organisationer}}."
                values={{ count: duplicateOrganizationIds.length }}
              />
            </ErrorBanner>
          )}

          {overlappingOrgCount > 0 && (
            <WarningBanner>
              <FormattedMessage
                defaultMessage="Perioden överlappar befintliga perioder hos {count, plural, one {# organisation} other {# organisationer}}. Elever kan få biljetter från flera perioder samtidigt."
                values={{ count: overlappingOrgCount }}
              />
            </WarningBanner>
          )}

          <div className="flex flex-col gap-4">
            {!isChildAndYouth && (
              <div className="flex flex-col gap-1">
                <p
                  id="tenant-period-ticket-kind-label"
                  className="text-base font-medium text-text-900"
                >
                  <FormattedMessage defaultMessage="Biljettyp" />
                </p>
                <ToggleButtonGroup
                  id="tenant-period-ticket-kind"
                  labelledBy="tenant-period-ticket-kind-label"
                  options={ticketKindOptions}
                  value={relational ? 'relational' : 'static'}
                  onChange={(value) => {
                    setFormRelational(value === 'relational');
                    setFormOfferStinaProductId(null);
                  }}
                />
                <p className="text-sm text-text-700">
                  {
                    ticketKindOptions.find(
                      (option) =>
                        option.value === (relational ? 'relational' : 'static'),
                    )?.tooltip
                  }
                </p>
              </div>
            )}

            {relational && (
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium text-text-900">
                  <FormattedMessage defaultMessage="Biljettkategorier" />
                </p>
                {selfManagedOrganizationIds.length > 0 && (
                  <p className="text-text-700">
                    {selfManagedOrganizationIds.length <
                    targetOrganizationIds.length ? (
                      <FormattedMessage
                        defaultMessage="Endast biljettkategorier som {count, plural, one {organisationen} other {alla organisationer}} med egen periodhantering har tillgång till kan väljas."
                        values={{ count: selfManagedOrganizationIds.length }}
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage="Endast biljettkategorier som {count, plural, one {den valda organisationen} other {alla valda organisationer}} har tillgång till kan väljas."
                        values={{ count: targetOrganizationIds.length }}
                      />
                    )}
                  </p>
                )}
                {targetOrganizationIds.length === 0 ? (
                  <p className="italic text-text-700">
                    <FormattedMessage defaultMessage="Välj minst en organisation för att se tillgängliga biljettkategorier." />
                  </p>
                ) : relationalProductsAccessFailed ? (
                  <ErrorBanner>
                    <FormattedMessage
                      defaultMessage="Kunde inte hämta vilka relationsbundna biljetter {count, plural, one {organisationen} other {organisationerna}} har tillgång till."
                      values={{ count: targetOrganizationIds.length }}
                    />
                  </ErrorBanner>
                ) : hasRelationalProducts === false ? (
                  <WarningBanner>
                    <FormattedMessage
                      defaultMessage="{count, plural, one {Den valda organisationen har inga relationsbundna biljetter} other {Alla valda organisationer har inte relationsbundna biljetter}} tillgängliga i den här kategorin. Relationsbundna perioder kan därför inte skapas. Välj statisk biljett eller ändra {count, plural, one {organisationens} other {organisationernas}} inställningar."
                      values={{ count: targetOrganizationIds.length }}
                    />
                  </WarningBanner>
                ) : (
                  <MultiSelectableList
                    options={tagOptions}
                    selectedOptions={periodTagIds}
                    onSelectedOptionsChange={handleTagSelectionChange}
                    isLoading={isLoadingTags}
                    noResultsMessage={formatMessage(
                      {
                        defaultMessage:
                          'Inga biljettkategorier är tillgängliga för {count, plural, one {den valda organisationen} other {alla valda organisationer}}.',
                      },
                      { count: targetOrganizationIds.length },
                    )}
                  />
                )}
                {tagRemovalBlocked && (
                  <WarningBanner icon={<IconWarningTriangle />}>
                    <FormattedMessage defaultMessage="Valda biljettkategorier kan inte tas bort eftersom en period varit aktiv den här eller föregående månaden. Nya biljettkategorier kan läggas till." />
                  </WarningBanner>
                )}
              </div>
            )}

            {!relational && (
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium text-text-900">
                  <FormattedMessage defaultMessage="Statisk biljett" />
                </p>
                {!allProductsAvailable && (
                  <p className="text-text-700">
                    {selfManagedOrganizationIds.length <
                    targetOrganizationIds.length ? (
                      <FormattedMessage
                        defaultMessage="Endast statiska biljetter som {count, plural, one {organisationen} other {alla organisationer}} med egen periodhantering har tillgång till kan väljas."
                        values={{ count: selfManagedOrganizationIds.length }}
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage="Endast statiska biljetter som {count, plural, one {den valda organisationen} other {alla valda organisationer}} har tillgång till kan väljas."
                        values={{ count: targetOrganizationIds.length }}
                      />
                    )}
                  </p>
                )}
                {targetOrganizationIds.length === 0 ? (
                  <p className="italic text-text-700">
                    <FormattedMessage defaultMessage="Välj minst en organisation för att se tillgängliga biljetter." />
                  </p>
                ) : offersAccessFailed ? (
                  <ErrorBanner>
                    <FormattedMessage
                      defaultMessage="Kunde inte hämta vilka biljetter {count, plural, one {organisationen} other {organisationerna}} har tillgång till."
                      values={{ count: targetOrganizationIds.length }}
                    />
                  </ErrorBanner>
                ) : (
                  <>
                    {offerSelectionUiMode === 'loading' && (
                      <SelectableList
                        options={[]}
                        selectedOption=""
                        setSelectedOption={handleSelectOffer}
                        isLoading
                      />
                    )}
                    {offerSelectionUiMode === 'empty' && (
                      <p className="italic text-text-700">
                        <FormattedMessage
                          defaultMessage="Inga statiska biljetter är tillgängliga för {count, plural, one {den valda organisationen} other {alla valda organisationer}}."
                          values={{ count: targetOrganizationIds.length }}
                        />
                      </p>
                    )}
                    {offerSelectionUiMode === 'single' && (
                      <p className="text-text-900">{offerOptions[0].label}</p>
                    )}
                    {offerSelectionUiMode === 'multiple' && (
                      <SelectableList
                        options={offerOptions}
                        selectedOption={
                          formOfferStinaProductId ?? offerOptions[0]?.id ?? ''
                        }
                        setSelectedOption={handleSelectOffer}
                        noResultsMessage={formatMessage(
                          {
                            defaultMessage:
                              'Inga statiska biljetter är tillgängliga för {count, plural, one {den valda organisationen} other {alla valda organisationer}}.',
                          },
                          { count: targetOrganizationIds.length },
                        )}
                      />
                    )}
                  </>
                )}
                {offerFormatError && (
                  <p className="text-red-800" role="alert">
                    {offerFormatError}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <p
              id="tenant-period-revoke-strategy-label"
              className="text-base font-medium text-text-900"
            >
              <FormattedMessage defaultMessage="Makuleringsstrategi" />
            </p>
            <p className="text-text-700">
              <FormattedMessage defaultMessage="Bestämmer vad som händer med periodens ej förbrukade biljetter när perioden tar slut. Elever som tas bort eller inaktiveras innan perioden tar slut får alltid sin biljett makulerad." />
            </p>
            <ToggleButtonGroup
              id="tenant-period-revoke-strategy"
              labelledBy="tenant-period-revoke-strategy-label"
              options={revokeStrategyOptions}
              value={formRevokeStrategy}
              onChange={(value) =>
                setFormRevokeStrategy(
                  value as CollectionTicketClaimRevokeStrategyDTO,
                )
              }
            />
            <p className="text-sm text-text-700">
              {
                revokeStrategyOptions.find(
                  (option) => option.value === formRevokeStrategy,
                )?.tooltip
              }
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <CheckBox
              id="tenant-period-non-invoiced"
              checked={formNonInvoiced}
              onCheckedChange={(checked) =>
                setFormNonInvoiced(checked === true)
              }
              label={formatMessage({ defaultMessage: 'Kostnadsfri period' })}
              aria-describedby={
                formNonInvoiced
                  ? 'tenant-period-non-invoiced-warning'
                  : undefined
              }
            />
            {formNonInvoiced && (
              <WarningBanner id="tenant-period-non-invoiced-warning">
                <FormattedMessage defaultMessage="Biljetter som ställs ut under perioden får inga transaktioner skapade och faktureras därmed inte." />
              </WarningBanner>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
            <Button
              variant="filled"
              onClick={handleCreate}
              loading={isSubmitting}
              disabled={!isFormValid}
              aria-describedby={
                duplicateOrganizationIds.length > 0
                  ? 'tenant-duplicate-period'
                  : undefined
              }
              startIcon={<IconCheck />}
            >
              <FormattedMessage defaultMessage="Skapa" />
            </Button>
          </div>
        </div>
      }
    />
  );
};
